import { useCallback, useState } from 'react';
import apiService from '../../../api/apiService';
import { RecipeComment } from '../../../api/Types';
import {
  deleteCommentItem,
  getCommentActionErrorMessage,
  updateCommentItem,
} from '../use-cases/commentItemActions';

interface UseCommentItemActionsOptions {
  comment: RecipeComment;
  isAuthor: boolean;
  token: string | null;
  onCommentUpdated: (updatedComment: RecipeComment) => void;
  onCommentDeleted: (commentId: number) => void;
}

interface UseCommentItemActionsResult {
  isEditing: boolean;
  editedComment: string;
  submitting: boolean;
  error: string;
  setEditedComment: React.Dispatch<React.SetStateAction<string>>;
  startEditing: () => void;
  cancelEditing: () => void;
  handleUpdate: () => Promise<void>;
  handleDelete: () => Promise<void>;
}

export const useCommentItemActions = ({
  comment,
  isAuthor,
  token,
  onCommentUpdated,
  onCommentDeleted,
}: UseCommentItemActionsOptions): UseCommentItemActionsResult => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedComment, setEditedComment] = useState(comment.comment);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const startEditing = useCallback(() => {
    setIsEditing(true);
  }, []);

  const cancelEditing = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleUpdate = useCallback(async () => {
    setSubmitting(true);
    setError('');

    try {
      const updated = await updateCommentItem({
        commentId: comment.id,
        editedComment,
        isAuthor,
        token,
        updateComment: apiService.updateComment,
      });
      if (!updated) {
        return;
      }

      onCommentUpdated(updated);
      setIsEditing(false);
    } catch (error) {
      setError(getCommentActionErrorMessage(error, 'Failed to update comment.'));
    } finally {
      setSubmitting(false);
    }
  }, [comment.id, editedComment, isAuthor, onCommentUpdated, token]);

  const handleDelete = useCallback(async () => {
    setSubmitting(true);
    setError('');

    try {
      const deleted = await deleteCommentItem({
        commentId: comment.id,
        confirmDelete: () => window.confirm('Voulez-vous vraiment supprimer ce commentaire ?'),
        deleteComment: apiService.deleteComment,
        isAuthor,
        token,
      });
      if (!deleted) {
        return;
      }

      onCommentDeleted(comment.id);
    } catch (error) {
      setError(getCommentActionErrorMessage(error, 'Failed to delete comment.'));
    } finally {
      setSubmitting(false);
    }
  }, [comment.id, isAuthor, onCommentDeleted, token]);

  return {
    isEditing,
    editedComment,
    submitting,
    error,
    setEditedComment,
    startEditing,
    cancelEditing,
    handleUpdate,
    handleDelete,
  };
};

export default useCommentItemActions;
